import type { AnyDomainEvent, Milli } from '@abastece/shared';
import { fromUnits, orderKey } from '@abastece/shared';
import { precioId, type ApplyResult, type Effect, type Producto, type Reader } from '../state.js';

export async function reduceProducto(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'producto.creado': {
      const p = event.payload;
      /**
       * Made once. Made again it was made over: another name, another unit, active again, and its
       * presentation — with the prices the owner gave it — moved to it (2026-10-13). Nothing the
       * shop does makes one twice; a doctored phone could.
       */
      if (await read.producto(p.productoId)) return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese producto ya existe' }] };
      if (await read.unidadVenta(p.unidadVenta.unidadVentaId)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa presentación ya existe' }] };
      }
      const effects: ApplyResult['effects'] = [
        {
          kind: 'producto.upsert',
          id: p.productoId,
          row: {
            id: p.productoId,
            nombre: p.nombre,
            nombreEn: p.nombreEn ?? null,
            categoria: p.categoria,
            unidadBase: p.unidadBase,
            esMedicamento: p.esMedicamento,
            perecedero: p.perecedero,
            registroSanitario: p.registroSanitario ?? null,
            codigosBarras: p.codigosBarras,
            foto: p.foto ?? null,
            stockMinimo: p.stockMinimo === undefined ? null : (p.stockMinimo as Milli),
            activo: true,
            rev,
          },
        },
        {
          kind: 'unidadVenta.upsert',
          id: p.unidadVenta.unidadVentaId,
          row: {
            id: p.unidadVenta.unidadVentaId,
            productoId: p.productoId,
            nombre: p.unidadVenta.nombre,
            factor: p.unidadVenta.factor as Milli,
            rev,
          },
        },
      ];

      // Opening prices ride along, so the bulk-entry screen queues one event per row.
      for (const precio of p.precios) {
        effects.push({
          kind: 'precio.set',
          id: precioId(p.unidadVenta.unidadVentaId, precio.listaId),
          row: {
            id: precioId(p.unidadVenta.unidadVentaId, precio.listaId),
            unidadVentaId: p.unidadVenta.unidadVentaId,
            listaId: precio.listaId,
            precio: precio.precio as never,
            rev,
          },
        });
      }

      return { effects, rejections: [] };
    }

    case 'producto.actualizado': {
      const p = event.payload;
      const existing = await read.producto(p.productoId);
      if (!existing) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'El producto no existe' }] };
      }
      const patch: Record<string, unknown> = {};
      if (p.nombre !== undefined) patch['nombre'] = p.nombre;
      if (p.nombreEn !== undefined) patch['nombreEn'] = p.nombreEn;
      if (p.categoria !== undefined) patch['categoria'] = p.categoria;
      if (p.esMedicamento !== undefined) patch['esMedicamento'] = p.esMedicamento;
      if (p.perecedero !== undefined) patch['perecedero'] = p.perecedero;
      if (p.registroSanitario !== undefined) patch['registroSanitario'] = p.registroSanitario ?? null;
      if (p.codigosBarras !== undefined) patch['codigosBarras'] = p.codigosBarras;
      if (p.foto !== undefined) patch['foto'] = p.foto;
      if (p.stockMinimo !== undefined) patch['stockMinimo'] = p.stockMinimo;
      if (p.unidadBase !== undefined) patch['unidadBase'] = p.unidadBase;
      const effects: Effect[] = [{ kind: 'producto.patch', id: p.productoId, rev, patch }];
      if (p.unidadBase !== undefined && p.unidadBase !== existing.unidadBase) {
        effects.push(...(await conOtraUnidad(existing, p.unidadBase, read, rev)));
      }
      return { effects, rejections: [] };
    }

    case 'producto.desactivado':
      return {
        effects: [
          { kind: 'producto.patch', id: event.payload.productoId, rev, patch: { activo: false } },
        ],
        rejections: [],
      };

    case 'producto.reactivado':
      return {
        effects: [
          { kind: 'producto.patch', id: event.payload.productoId, rev, patch: { activo: true } },
        ],
        rejections: [],
      };

    case 'unidad_venta.creada': {
      const p = event.payload;
      const producto = await read.producto(p.productoId);
      if (!producto) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'El producto no existe' }] };
      }
      // Made again, it moved to another product with the prices the owner gave it (`producto.creado`).
      if (await read.unidadVenta(p.unidadVentaId)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa presentación ya existe' }] };
      }
      return {
        effects: [
          {
            kind: 'unidadVenta.upsert',
            id: p.unidadVentaId,
            row: {
              id: p.unidadVentaId,
              productoId: p.productoId,
              nombre: p.nombre,
              factor: p.factor as Milli,
              codigosBarras: p.codigosBarras,
              rev,
            },
          },
        ],
        rejections: [],
      };
    }

    case 'unidad_venta.actualizada': {
      const p = event.payload;
      const existing = await read.unidadVenta(p.unidadVentaId);
      if (!existing) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La unidad no existe' }] };
      }
      const patch: Record<string, unknown> = {};
      if (p.nombre !== undefined) patch['nombre'] = p.nombre;
      if (p.factor !== undefined) patch['factor'] = p.factor;
      if (p.codigosBarras !== undefined) patch['codigosBarras'] = p.codigosBarras;
      return {
        effects: [{ kind: 'unidadVenta.patch', id: p.unidadVentaId, rev, patch }],
        rejections: [],
      };
    }

    case 'unidad_venta.eliminada':
      return {
        effects: [{ kind: 'unidadVenta.delete', id: event.payload.unidadVentaId, rev }],
        rejections: [],
      };

    default:
      return null;
  }
}

/**
 * What else changes when a product's stock goes into another unit: only the unit's name, with
 * stock or without (the owner, 2026-10-04). It is how a product created in the wrong unit is put
 * right, so every number stays as it was counted: 50 «unidad» are 50 «libra» now, and the
 * presentations' factors, the minimum, the lots and the cost per unit stay as they were.
 *
 * The presentation that is the unit itself, one of it, made with the product and named after it,
 * takes the new name too: the till, the receipt and the customer's screen say which presentation
 * a line is, and would go on selling the rice «por unidad». A presentation named anything else is
 * somebody's own word, and stays.
 *
 * A rule other than renaming (converting what is counted, or no change while there is stock)
 * goes here, and only here.
 */
async function conOtraUnidad(producto: Producto, unidadBase: string, read: Reader, rev: number): Promise<Effect[]> {
  const unidades = await read.unidadesDeProducto(producto.id);
  return unidades
    .filter((u) => u.factor === fromUnits(1) && u.nombre === producto.unidadBase)
    .map((u): Effect => ({ kind: 'unidadVenta.patch', id: u.id, rev, patch: { nombre: unidadBase } }));
}
